import { DateTime, Interval } from 'luxon';
import { CampaignState, CampaignStatus, CampaignFilters, CampaignWithLowPerformance } from 'types';
import { formatDate, getCeilDays, getDayWord } from './date';

export const getChipColor = (
  status: CampaignStatus
): {
  backgroundColor: string;
  color: string;
  borderColor: string;
} => {
  switch (status) {
    case CampaignStatus.active: {
      return {
        backgroundColor: 'success.light',
        color: 'success.dark',
        borderColor: 'success.main',
      };
    }
    case CampaignStatus.draft: {
      return {
        backgroundColor: 'primary.light',
        color: 'primary.main',
        borderColor: 'rose.200',
      };
    }

    case CampaignStatus.over:
    case CampaignStatus.stopped: {
      return {
        backgroundColor: 'grey.200',
        color: 'grey.700',
        borderColor: 'grey.300',
      };
    }
    case CampaignStatus.ready: {
      return {
        backgroundColor: 'blue.50',
        color: 'blue.800',
        borderColor: 'blue.200',
      };
    }
  }
};

export const getCampaignStatus = ({
  state,
  startDate,
  endDate,
  isStopped,
}: {
  state: CampaignState;
  startDate: string | null;
  endDate: string | null;
  isStopped?: boolean;
}) => {
  if (isStopped) {
    return CampaignStatus.stopped;
  }
  if (state === CampaignState.draft) {
    return CampaignStatus.draft;
  }
  if (!endDate || !startDate) {
    return CampaignStatus.ready;
  }
  const now = DateTime.now();
  const interval = Interval.fromDateTimes(DateTime.fromISO(startDate), DateTime.fromISO(endDate));
  if (interval.contains(now)) return CampaignStatus.active;
  if (interval.isBefore(now)) return CampaignStatus.over;
  return CampaignStatus.ready;
};

export const getDateDescription = (
  status: CampaignStatus,
  start: string | null,
  end: string | null,
  updated: string
) => {
  const now = DateTime.now();
  const startDate = DateTime.fromISO(start || '');
  const endDate = DateTime.fromISO(end || '');
  const updatedAt = DateTime.fromISO(updated);

  switch (status) {
    case CampaignStatus.over: {
      const diff = endDate.diff(startDate, ['days']).toObject();
      return `Lief ${getCeilDays(diff.days)} ${getDayWord(diff.days)} - Beendet am ${formatDate(endDate, 'dd.MM.yyyy')}`;
    }
    case CampaignStatus.draft: {
      const diff = now.diff(updatedAt, ['days', 'hours', 'minutes']).toObject();
      const restTime =
        diff?.days! > 0
          ? `${getCeilDays(diff.days)} ${getDayWord(diff.days, 'dative')}, ${Math.floor(diff.hours!)} Stunden`
          : diff?.hours! >= 1
            ? `${Math.floor(diff.hours!)} Stunden`
            : diff?.minutes! >= 1
              ? `${Math.floor(diff.minutes!)} Minuten`
              : 'einigen Sekunden';
      return `Zuletzt bearbeitet vor ${restTime}`;
    }
    case CampaignStatus.active: {
      const diff = endDate.diff(now, ['days', 'hours']).toObject();
      const restTime =
        diff?.days! > 0
          ? `${getCeilDays(diff.days)} ${getDayWord(diff.days)}, ${Math.floor(diff.hours!)} Stunden`
          : `${Math.floor(diff.hours!)} Stunden`;
      return `Läuft noch ${restTime}`;
    }
    case CampaignStatus.ready: {
      const diff = startDate.diff(now, ['days', 'hours']).toObject();
      const restTime =
        diff?.days! > 0
          ? `${getCeilDays(diff.days)} ${getDayWord(diff.days, 'dative')}, ${Math.floor(diff.hours!)} Stunden`
          : `${Math.floor(diff.hours!)} Stunden`;
      return `Startet in ${restTime}`;
    }
    case CampaignStatus.stopped: {
      const diff = startDate.diff(now, ['days', 'hours']).toObject();

      return `Lief ${diff.days} Tage - Vorzeitig beendet am ${formatDate(endDate, 'dd.MM.yyyy')}`;
    }
  }
};

export const getFilteredCampaigns = (filter: CampaignFilters, campaigns: CampaignWithLowPerformance[]) => {
  switch (filter) {
    case CampaignFilters.ALL:
      return campaigns;

    case CampaignFilters.ACTIVE:
      return campaigns.filter((campaign) => {
        const interval =
          campaign.endDate && campaign.startDate
            ? Interval.fromDateTimes(DateTime.fromISO(campaign.startDate), DateTime.fromISO(campaign.endDate))
            : null;
        const now = DateTime.now();
        return campaign.state === CampaignState.ready && interval?.contains(now);
      });

    case CampaignFilters.DRAFT:
      return campaigns.filter((campaign) => campaign.state === CampaignState.draft);

    case CampaignFilters.ENDED_STOPPED:
      return campaigns.filter((campaign) => {
        const endDate = campaign.endDate && DateTime.fromISO(campaign.endDate).startOf('day');
        const now = DateTime.now().startOf('day');
        return (campaign.state === CampaignState.ready && endDate && endDate < now) || campaign.isStopped;
      });

    default:
      return campaigns;
  }
};
